import { useState, useMemo, useRef, useEffect } from "react";
import "./style.css";

// Normaliza para búsqueda insensible a mayúsculas Y a tildes: "Bío Bío" → "bio bio".
const norm = (s) =>
  String(s ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

/** Combobox con buscador inline. options = [{value, label, hint}]. */
export default function SearchSelect({
  value, onChange, options,
  placeholder = "Seleccionar…",
  disabled = false,
  searchPlaceholder = "Buscar…",
  emptyText = "Sin resultados",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false); setQuery("");
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") { setOpen(false); setQuery(""); }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    // Focus en el input de búsqueda al abrir
    setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = norm(query);
    return options.filter((o) =>
      norm(o.label).includes(q) || norm(o.value).includes(q),
    );
  }, [options, query]);

  const current = options.find((o) => o.value === value);
  const currentLabel = current?.label || placeholder;

  return (
    <div ref={wrapRef} className={`ss ${disabled ? "ss-disabled" : ""}`}>
      <button type="button" className="ss-trigger"
              onClick={() => !disabled && setOpen((o) => !o)}
              disabled={disabled}>
        <span className="ss-value" title={String(currentLabel)}>{currentLabel}</span>
        <span className="ss-caret">▾</span>
      </button>
      {open && (
        <div className="ss-pop">
          <input ref={inputRef}
                 className="ss-search"
                 placeholder={searchPlaceholder}
                 value={query}
                 onChange={(e) => setQuery(e.target.value)} />
          <div className="ss-list">
            {filtered.length === 0 ? (
              <div className="ss-empty">{emptyText}</div>
            ) : filtered.map((o) => (
              <button key={o.value || "__none__"} type="button"
                      className={`ss-opt ${o.value === value ? "ss-opt-active" : ""}`}
                      onClick={() => { onChange(o.value); setOpen(false); setQuery(""); }}>
                <span className="ss-opt-label">{o.label}</span>
                {o.hint != null && <span className="ss-opt-hint">{o.hint}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
